<template>
  <div class="instructor-dashboard pa-10">

    <!-- ── Header ── -->
    <div class="d-flex flex-wrap justify-space-between align-center mb-7 gap-3">
      <div>
        <div class="dash-page-title">Instructor Dashboard</div>
        <div class="dash-page-sub mt-1">Monitor Intern Progress &amp; Document Validation</div>
      </div>
      <div class="date-badge">{{ currentDate }}</div>
    </div>

    <!-- ── Stat Cards ── -->
    <v-row class="mb-5" style="gap: 0;">
      <v-col cols="12" sm="6" lg="3">
        <div class="stat-card stat-card--blue rounded-xl pa-5">
          <div class="d-flex justify-space-between align-start mb-4">
            <div class="stat-icon-box stat-icon-box--blue rounded-lg pa-2">
              <v-icon size="22" color="#2563eb">mdi-account-multiple-outline</v-icon>
            </div>
            <v-icon size="18" color="#2563eb">mdi-account-plus-outline</v-icon>
          </div>
          <div class="stat-number stat-number--blue">{{ stats.total_interns }}</div>
          <div class="stat-label stat-label--blue">My Interns</div>
          <div class="stat-sub">{{ stats.active_interns }} Active, {{ stats.completed_interns }} Completed</div>
        </div>
      </v-col>

      <v-col cols="12" sm="6" lg="3">
        <div class="stat-card stat-card--amber rounded-xl pa-5">
          <div class="d-flex justify-space-between align-start mb-4">
            <div class="stat-icon-box stat-icon-box--amber rounded-lg pa-2">
              <v-icon size="22" color="#d97706">mdi-file-document-edit-outline</v-icon>
            </div>
            <v-icon size="18" color="#d97706">mdi-alert-outline</v-icon>
          </div>
          <div class="stat-number stat-number--amber">{{ stats.pending_validation }}</div>
          <div class="stat-label stat-label--amber">Pending Validation</div>
          <div class="stat-sub">Documents to review</div>
        </div>
      </v-col>

      <v-col cols="12" sm="6" lg="3">
        <div class="stat-card stat-card--green rounded-xl pa-5">
          <div class="d-flex justify-space-between align-start mb-4">
            <div class="stat-icon-box stat-icon-box--green rounded-lg pa-2">
              <v-icon size="22" color="#059669">mdi-check-circle-outline</v-icon>
            </div>
            <v-icon size="18" color="#059669">mdi-trending-up</v-icon>
          </div>
          <div class="stat-number stat-number--green">{{ stats.attendance_rate }}%</div>
          <div class="stat-label stat-label--green">Attendance Rate</div>
          <div class="stat-sub">Last 30 days</div>
        </div>
      </v-col>

      <v-col cols="12" sm="6" lg="3">
        <div class="stat-card stat-card--yellow rounded-xl pa-5">
          <div class="d-flex justify-space-between align-start mb-4">
            <div class="stat-icon-box stat-icon-box--yellow rounded-lg pa-2">
              <v-icon size="22" color="#ca8a04">mdi-medal-outline</v-icon>
            </div>
            <v-icon size="18" color="#ca8a04">mdi-trending-up</v-icon>
          </div>
          <div class="stat-number stat-number--yellow">{{ stats.avg_performance }}</div>
          <div class="stat-label stat-label--yellow">Avg Performance</div>
          <div class="stat-sub">Out of 5.0</div>
        </div>
      </v-col>
    </v-row>

    <!-- ── Charts Row 1 ── -->
    <v-row class="mb-5">
      <v-col cols="12" md="6">
        <div class="dash-card rounded-xl pa-6">
          <div class="d-flex justify-space-between align-center mb-1">
            <div class="card-title">Intern Hours Progress</div>
            <v-icon size="20" color="#94a3b8">mdi-clock-outline</v-icon>
          </div>
          <div class="card-sub mb-5">Individual student training hours vs. 486-hour target</div>
          <div class="chart-wrap">
            <canvas ref="hoursChart"></canvas>
          </div>
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <div class="dash-card rounded-xl pa-6">
          <div class="d-flex justify-space-between align-center mb-1">
            <div class="card-title">Document Validation Status</div>
            <v-icon size="20" color="#94a3b8">mdi-clipboard-text-outline</v-icon>
          </div>
          <div class="card-sub mb-5">Breakdown of document validation tasks</div>
          <div class="chart-wrap chart-wrap--doughnut">
            <canvas ref="validationChart"></canvas>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- ── Charts Row 2 ── -->
    <v-row class="mb-5">
      <v-col cols="12" md="6">
        <div class="dash-card rounded-xl pa-6">
          <div class="d-flex justify-space-between align-center mb-1">
            <div class="card-title">Weekly Attendance Overview</div>
            <v-icon size="20" color="#94a3b8">mdi-calendar-outline</v-icon>
          </div>
          <div class="card-sub mb-5">Attendance tracking for the past 4 weeks</div>
          <div class="chart-wrap">
            <canvas ref="attendanceChart"></canvas>
          </div>
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <div class="dash-card rounded-xl pa-6">
          <div class="d-flex justify-space-between align-center mb-1">
            <div class="card-title">Performance Rating Distribution</div>
            <v-icon size="20" color="#94a3b8">mdi-medal-outline</v-icon>
          </div>
          <div class="card-sub mb-5">Student evaluation score distribution</div>
          <div class="chart-wrap">
            <canvas ref="performanceChart"></canvas>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- ── Quick Actions ── -->
    <div class="dash-card rounded-xl pa-6 mb-5">
      <div class="d-flex justify-space-between align-center mb-5">
        <div class="card-title">Quick Actions</div>
        <a class="view-all-link">View All →</a>
      </div>
      <v-row>
        <v-col cols="12" sm="4">
          <div class="action-card rounded-xl pa-5">
            <div class="action-icon-box action-icon-box--amber rounded-lg pa-2 mb-3">
              <v-icon color="#d97706" size="22">mdi-clipboard-check-outline</v-icon>
            </div>
            <div class="action-title">Validate Documents</div>
            <div class="action-sub">{{ stats.pending_validation }} documents awaiting review</div>
          </div>
        </v-col>
        <v-col cols="12" sm="4">
          <div class="action-card rounded-xl pa-5">
            <div class="action-icon-box action-icon-box--blue rounded-lg pa-2 mb-3">
              <v-icon color="#2563eb" size="22">mdi-account-multiple-outline</v-icon>
            </div>
            <div class="action-title">Monitor Interns</div>
            <div class="action-sub">View all intern profiles</div>
          </div>
        </v-col>
        <v-col cols="12" sm="4">
          <div class="action-card rounded-xl pa-5">
            <div class="action-icon-box action-icon-box--purple rounded-lg pa-2 mb-3">
              <v-icon color="#7c3aed" size="22">mdi-qrcode</v-icon>
            </div>
            <div class="action-title">Generate QR Code</div>
            <div class="action-sub">Create attendance QR</div>
          </div>
        </v-col>
      </v-row>
    </div>

    <!-- ── Recent Document Submissions ── -->
    <div class="dash-card rounded-xl pa-6">
      <div class="card-title mb-5">Recent Document Submissions</div>

      <div v-if="loadingSubmissions" class="d-flex justify-center py-6">
        <v-progress-circular indeterminate color="#3b82f6" size="32" />
      </div>

      <template v-else>
        <div v-if="!recentSubmissions.length" class="card-sub py-4 text-center">
          No recent submissions.
        </div>
        <div
          v-for="(sub, i) in recentSubmissions"
          :key="sub.id"
          class="submission-item rounded-xl pa-4"
          :class="{ 'mb-3': i < recentSubmissions.length - 1 }"
        >
          <div class="d-flex align-center justify-space-between" style="gap: 12px;">
            <div class="d-flex align-center" style="gap: 14px; flex: 1; min-width: 0;">
              <div
                class="sub-icon-box rounded-lg pa-2 flex-shrink-0"
                :style="{ background: sub.status === 'validated' ? '#dcfce7' : '#fff7ed' }"
              >
                <v-icon
                  size="20"
                  :color="sub.status === 'validated' ? '#059669' : '#d97706'"
                >
                  {{ sub.status === 'validated' ? 'mdi-check-circle-outline' : 'mdi-file-document-outline' }}
                </v-icon>
              </div>
              <div style="min-width: 0;">
                <div class="sub-name">{{ sub.student_name }} – {{ sub.document_type }}</div>
                <div class="sub-detail">{{ sub.document_detail }} • {{ sub.file_type }}</div>
                <div class="d-flex align-center mt-1" style="gap: 8px;">
                  <v-icon size="13" color="#9ca3af">mdi-clock-outline</v-icon>
                  <span class="sub-time">{{ sub.time_ago }}</span>
                  <span
                    class="sub-status-badge"
                    :class="sub.status === 'validated' ? 'sub-status-badge--green' : 'sub-status-badge--amber'"
                  >
                    {{ sub.status === 'validated' ? 'Validated' : 'Pending Review' }}
                  </span>
                </div>
              </div>
            </div>
            <div v-if="sub.status === 'pending'" class="d-flex" style="gap: 8px; flex-shrink: 0;">
              <v-btn
                size="small"
                color="#10b981"
                variant="flat"
                rounded="lg"
                class="text-white"
                :loading="sub._validating"
                @click="validateDocument(sub)"
              >Validate</v-btn>
              <v-btn
                size="small"
                color="#ef4444"
                variant="flat"
                rounded="lg"
                class="text-white"
                :loading="sub._rejecting"
                @click="rejectDocument(sub)"
              >Reject</v-btn>
            </div>
          </div>
        </div>
      </template>
    </div>

  </div>
</template>

<script>
import Chart from "chart.js/auto";

export default {
  name: "InstructorDashboard",
  data() {
    return {
      charts: {},
      loading: false,
      stats: {
        total_interns: 0,
        active_interns: 0,
        completed_interns: 0,
        pending_validation: 0,
        attendance_rate: 0,
        avg_performance: 0,
      },
      internHoursData: [],
      validationData: { pending: 0, validated: 0, invalidated: 0 },
      attendanceData: [],
      performanceData: [],
      recentSubmissions: [],
      loadingSubmissions: false,
    };
  },
  computed: {
    currentDate() {
      return new Date().toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
  },
  mounted() {
    this.fetchDashboardData();
    this.fetchRecentSubmissions();
  },
  beforeUnmount() {
    Object.values(this.charts).forEach((c) => c && c.destroy());
  },
  methods: {
    async fetchDashboardData() {
      this.loading = true;
      try {
        const res = await this.axiosCall("/student/instructor-dashboard", "GET");
        if (res && res.data) {
          const d = res.data;
          if (d.stats) this.stats = { ...this.stats, ...d.stats };
          if (d.intern_hours) this.internHoursData = d.intern_hours;
          if (d.validation_status) this.validationData = d.validation_status;
          if (d.attendance_weekly) this.attendanceData = d.attendance_weekly;
          if (d.performance_distribution) this.performanceData = d.performance_distribution;
        }
      } catch (e) {
        console.error("Failed to load instructor dashboard", e);
      } finally {
        this.loading = false;
        this.$nextTick(() => {
          this.initHoursChart();
          this.initValidationChart();
          this.initAttendanceChart();
          this.initPerformanceChart();
        });
      }
    },

    initHoursChart() {
      if (this.charts.hours) this.charts.hours.destroy();
      const TARGET = 486;
      const labels = this.internHoursData.length
        ? this.internHoursData.map((d) => d.name)
        : ["Juan Dela Cruz", "Maria Clara", "Jose Rizal", "Andres Bonifacio", "Apolinario Mabini"];
      const rendered = this.internHoursData.length
        ? this.internHoursData.map((d) => d.hours)
        : [320, 410, 280, 350, 290];
      const targets = labels.map(() => TARGET);

      this.charts.hours = new Chart(this.$refs.hoursChart, {
        type: "bar",
        data: {
          labels,
          datasets: [
            {
              label: "Hours Rendered",
              data: rendered,
              backgroundColor: "#3b82f6",
              borderRadius: 4,
              barPercentage: 0.4,
              categoryPercentage: 0.7,
            },
            {
              label: "Target (486h)",
              data: targets,
              backgroundColor: "#e5e7eb",
              borderRadius: 4,
              barPercentage: 0.4,
              categoryPercentage: 0.7,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: "bottom",
              labels: {
                usePointStyle: true,
                pointStyle: "circle",
                padding: 16,
                font: { size: 11 },
                color: "#6b7280",
              },
            },
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: { color: "#9ca3af", font: { size: 10 }, maxRotation: 30 },
              border: { display: false },
            },
            y: {
              beginAtZero: true,
              grid: { color: "#f3f4f6" },
              ticks: { color: "#9ca3af", font: { size: 11 } },
              border: { display: false },
            },
          },
        },
      });
    },

    initValidationChart() {
      if (this.charts.validation) this.charts.validation.destroy();
      const d = this.validationData;
      const pending = d.pending ?? 12;
      const validated = d.validated ?? 45;
      const invalidated = d.invalidated ?? 8;

      this.charts.validation = new Chart(this.$refs.validationChart, {
        type: "doughnut",
        data: {
          labels: ["Pending", "Validated", "Invalidated"],
          datasets: [
            {
              data: [pending, validated, invalidated],
              backgroundColor: ["#f59e0b", "#10b981", "#ef4444"],
              borderWidth: 3,
              borderColor: "#fff",
              hoverOffset: 6,
            },
          ],
        },
        options: {
          cutout: "68%",
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: "bottom",
              labels: {
                usePointStyle: true,
                pointStyle: "circle",
                padding: 20,
                font: { size: 12 },
                color: "#6b7280",
              },
            },
          },
        },
      });
    },

    initAttendanceChart() {
      if (this.charts.attendance) this.charts.attendance.destroy();
      const labels = this.attendanceData.length
        ? this.attendanceData.map((d) => d.week)
        : ["Week 1", "Week 2", "Week 3", "Week 4"];
      const present = this.attendanceData.length
        ? this.attendanceData.map((d) => d.present)
        : [13, 14, 15, 14];
      const late = this.attendanceData.length
        ? this.attendanceData.map((d) => d.late)
        : [2, 1, 0, 1];
      const absent = this.attendanceData.length
        ? this.attendanceData.map((d) => d.absent)
        : [0, 0, 0, 0];

      this.charts.attendance = new Chart(this.$refs.attendanceChart, {
        type: "bar",
        data: {
          labels,
          datasets: [
            {
              label: "Present",
              data: present,
              backgroundColor: "#10b981",
              borderRadius: 4,
              barPercentage: 0.35,
              categoryPercentage: 0.75,
            },
            {
              label: "Late",
              data: late,
              backgroundColor: "#f59e0b",
              borderRadius: 4,
              barPercentage: 0.35,
              categoryPercentage: 0.75,
            },
            {
              label: "Absent",
              data: absent,
              backgroundColor: "#ef4444",
              borderRadius: 4,
              barPercentage: 0.35,
              categoryPercentage: 0.75,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: "bottom",
              labels: {
                usePointStyle: true,
                pointStyle: "circle",
                padding: 16,
                font: { size: 11 },
                color: "#6b7280",
              },
            },
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: { color: "#9ca3af", font: { size: 11 } },
              border: { display: false },
            },
            y: {
              beginAtZero: true,
              grid: { color: "#f3f4f6" },
              ticks: { color: "#9ca3af", font: { size: 11 } },
              border: { display: false },
            },
          },
        },
      });
    },

    initPerformanceChart() {
      if (this.charts.performance) this.charts.performance.destroy();
      const labels = ["5.0", "4.5–4.9", "4.0–4.4", "3.5–3.9", "<3.5"];
      const data = this.performanceData.length
        ? this.performanceData.map((d) => d.count)
        : [1, 3, 7, 3, 1];

      this.charts.performance = new Chart(this.$refs.performanceChart, {
        type: "bar",
        data: {
          labels,
          datasets: [
            {
              label: "Students",
              data,
              backgroundColor: "#8b5cf6",
              borderRadius: 4,
              barPercentage: 0.5,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            x: {
              grid: { display: false },
              ticks: { color: "#9ca3af", font: { size: 11 } },
              border: { display: false },
            },
            y: {
              beginAtZero: true,
              grid: { color: "#f3f4f6" },
              ticks: { color: "#9ca3af", font: { size: 11 }, stepSize: 2 },
              border: { display: false },
            },
          },
        },
      });
    },

    async fetchRecentSubmissions() {
      this.loadingSubmissions = true;
      try {
        const res = await this.axiosCall("/student/instructor-recent-submissions", "GET");
        if (res && res.data) {
          this.recentSubmissions = res.data.map((s) => ({ ...s, _validating: false, _rejecting: false }));
        }
      } catch (e) {
        console.error("Failed to load recent submissions", e);
      } finally {
        this.loadingSubmissions = false;
      }
    },

    async validateDocument(sub) {
      sub._validating = true;
      try {
        await this.axiosCall(`/student-requirement-submission/approve/${sub.id}`, "PATCH");
        sub.status = "validated";
      } catch (e) {
        console.error("Failed to validate document", e);
      } finally {
        sub._validating = false;
      }
    },

    async rejectDocument(sub) {
      sub._rejecting = true;
      try {
        await this.axiosCall("/student-requirement-submission/saveDisapproval", "POST", {
          id: sub.id,
          remarks: "",
        });
        sub.status = "invalidated";
      } catch (e) {
        console.error("Failed to reject document", e);
      } finally {
        sub._rejecting = false;
      }
    },
  },
};
</script>

<style scoped>
.instructor-dashboard {
  min-height: 100vh;
}

/* ── Header ── */
.dash-page-title {
  font-size: 30px;
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.5px;
  line-height: 1.2;
}

.dash-page-sub {
  font-size: 14px;
  color: #6b7280;
}

.date-badge {
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 6px 14px;
  background: #fff;
}

/* ── Stat Cards ── */
.stat-card {
  border: 1px solid transparent;
  transition: box-shadow 0.2s;
  height: 100%;
}
.stat-card:hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.09);
}
.stat-card--blue   { background: #eff6ff; border-color: #bfdbfe; }
.stat-card--amber  { background: #fffbeb; border-color: #fde68a; }
.stat-card--green  { background: #ecfdf5; border-color: #a7f3d0; }
.stat-card--yellow { background: #fefce8; border-color: #fef08a; }

.stat-icon-box {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.stat-icon-box--blue   { background: #bfdbfe; }
.stat-icon-box--amber  { background: #fde68a; }
.stat-icon-box--green  { background: #a7f3d0; }
.stat-icon-box--yellow { background: #fef3c7; }

.stat-number {
  font-size: 40px;
  font-weight: 800;
  line-height: 1.05;
  letter-spacing: -1.5px;
}
.stat-number--blue   { color: #2563eb; }
.stat-number--amber  { color: #d97706; }
.stat-number--green  { color: #059669; }
.stat-number--yellow { color: #ca8a04; }

.stat-label {
  font-size: 13px;
  font-weight: 600;
  margin-top: 6px;
}
.stat-label--blue   { color: #2563eb; }
.stat-label--amber  { color: #d97706; }
.stat-label--green  { color: #059669; }
.stat-label--yellow { color: #ca8a04; }

.stat-sub {
  font-size: 11.5px;
  color: #9ca3af;
  margin-top: 3px;
}

/* ── Dash Cards ── */
.dash-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.04);
}

.card-title {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}

.card-sub {
  font-size: 12.5px;
  color: #9ca3af;
}

/* ── Charts ── */
.chart-wrap {
  position: relative;
  width: 100%;
  height: 230px;
}

.chart-wrap--doughnut {
  height: 210px;
}

/* ── Quick Actions ── */
.view-all-link {
  font-size: 13px;
  color: #10b981;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
}
.view-all-link:hover {
  text-decoration: underline;
}

.action-card {
  border: 1px solid #e5e7eb;
  cursor: pointer;
  transition: box-shadow 0.15s, border-color 0.15s;
  height: 100%;
}
.action-card:hover {
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.07);
  border-color: #dbeafe;
}
.action-card--active {
  background: #eff6ff;
  border-color: #bfdbfe;
}

.action-icon-box {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.action-icon-box--amber  { background: #fde68a; }
.action-icon-box--blue   { background: #bfdbfe; }
.action-icon-box--purple { background: #ddd6fe; }

.action-title {
  font-size: 13.5px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 3px;
}

.action-sub {
  font-size: 12px;
  color: #9ca3af;
}

/* ── Recent Submissions ── */
.submission-item {
  background: #fafafa;
  border: 1px solid #f3f4f6;
  transition: border-color 0.15s;
}
.submission-item:hover {
  border-color: #e5e7eb;
}

.sub-icon-box {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.sub-name {
  font-size: 13.5px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sub-detail {
  font-size: 12px;
  color: #6b7280;
}

.sub-time {
  font-size: 12px;
  color: #9ca3af;
}

.sub-status-badge {
  font-size: 11.5px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
}
.sub-status-badge--amber {
  background: #fff7ed;
  color: #d97706;
}
.sub-status-badge--green {
  background: #ecfdf5;
  color: #059669;
}
</style>
